import { useMemo, useRef, useEffect, useState, forwardRef } from "react";
import HTMLFlipBook from "react-pageflip";
import BookPage from "./BookPage";

/**
 * 3D page-flip book for a Hebrew Tanakh.
 *
 * RTL STRATEGY — native library flip + reversed rendering (no CSS mirror):
 *   ───────────────────────────────────────────────────────────────────
 *   react-pageflip is hard-wired LTR (no rtl option). To make it behave
 *   like a Hebrew sefer without fighting its gesture engine — so the page
 *   physically follows the finger in the correct direction — we:
 *
 *   1. Keep `pages` (our semantic model) in NATURAL reading order:
 *        pages[0] = Bereshit 1:1, pages[N] = last page.
 *      This is the single source of truth and is never mutated.
 *
 *   2. Feed the library `renderedPages = [...pages].reverse()`, i.e. we
 *      hand pages to the LTR library in REVERSE order. This is a rendering
 *      concern only — semantic data stays forward.
 *
 *   3. `startPage = renderedPages.length - 1` → library opens to what it
 *      considers "the end". Because we reversed, that "end" holds page 0
 *      (Bereshit 1:1) and lands on the library's RIGHT side of the spread.
 *
 *   4. No CSS mirror / scaleX(-1) anywhere. Hebrew text reads natively via
 *      `dir="rtl"` on the verse container.
 *
 *   Result:
 *     • First spread visible:   RIGHT = Bereshit 1:1, LEFT = continuation
 *     • Swipe RIGHT (dx > 0)  → library fires flipPrev
 *       → the LEFT page physically bends RIGHTWARD following the finger
 *       → next spread [p3 on right, p4 on left] is revealed underneath
 *     • Swipe LEFT           → flipNext → goes BACK a spread (no-op at
 *       the start, as intended — you cannot read earlier than Bereshit 1:1).
 *
 *   This is the same direction as flipping a real Hebrew sefer: you grab
 *   the LEFT leaf and turn it to the RIGHT to advance.
 *
 * Exposes .flipNext() / .flipPrev() / .goTo() via ref; `flipForward()` and
 * `flipBackward()` helpers map the human-level intent to the library call.
 */
const BookReader3D = forwardRef(function BookReader3D(
  {
    verses = [],
    bookHe,
    bookEn,
    chapter,
    totalChapters,
    onVerseClick,
    activeVerseNum,
    highlights = {},
    notesByVerse = {},
    nikud = true,
    showVerseNums = true,
    onPageFlip,
    tocPages = [], // optional extra pages prepended (e.g., parchment TOC)
    // ---- Continuous reading (new) ----
    onPrevChapter,       // () => navigate to prev chapter
    onNextChapter,       // () => navigate to next chapter
    hasPrev = false,
    hasNext = false,
    autoContinue = false, // "קריאה רציפה" — auto-load on first-page back / last-page forward
    // "המשך לפרק הבא" by default; Talmud passes "המשך לדף הבא" so the
    // inter-amud transition reads correctly to the user.
    nextChapterLabelHe = "המשך לפרק הבא",
    // When the current book uses Talmud-style labelling, the parent
    // passes a function that converts a chapter index → its full Hebrew
    // daf+amud label (e.g. 1 → "דף ב׳ ע״א"). When undefined, BookPage
    // falls back to the standard "פרק <gematria>" rendering.
    chapterLabelFn,
    // Same as chapterLabelFn but for the English running-head (e.g.
    // 1 → "Daf 2a"). Without it, the page header would still show the
    // meaningless sequential integer.
    chapterLabelEnFn,
  },
  ref
) {
  const bookRef = useRef(null);
  const stageRef = useRef(null);
  // Ref-mirrors of the inputs that `goToVerse` needs to consult — kept in
  // sync below so the imperative API never closes over stale values.
  const pagingRef = useRef({ verses, tocPages, hasPrev, pages: [] });
  // Track the visible stage width so the book fits its actual container
  // (e.g. the center column of the desktop 3-panel shell) rather than
  // overflowing the whole viewport.
  const [stageWidth, setStageWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1200
  );
  const [dims, setDims] = useState(() =>
    computeDims(
      typeof window !== "undefined" ? window.innerWidth : 1200,
      typeof window !== "undefined" ? window.innerHeight : 900
    )
  );

  useEffect(() => {
    const el = stageRef.current;
    const recompute = () => {
      const w = el?.getBoundingClientRect?.()?.width || window.innerWidth;
      setStageWidth(Math.round(w));
      setDims(computeDims(w, window.innerHeight));
    };
    if (!el || typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", recompute);
      window.addEventListener("orientationchange", recompute);
      return () => {
        window.removeEventListener("resize", recompute);
        window.removeEventListener("orientationchange", recompute);
      };
    }
    const ro = new ResizeObserver((entries) => {
      const w = Math.round(entries[0]?.contentRect?.width || window.innerWidth);
      setStageWidth(w);
    });
    ro.observe(el);
    // On orientation change the viewport HEIGHT also changes (which the
    // ResizeObserver above won't notice if container width happens to be
    // identical). Force a recompute so the book never gets clipped after
    // rotating the phone landscape↔portrait.
    window.addEventListener("orientationchange", recompute);
    return () => {
      ro.disconnect();
      window.removeEventListener("orientationchange", recompute);
    };
  }, []);

  useEffect(() => {
    setDims(computeDims(stageWidth, window.innerHeight));
  }, [stageWidth]);

  // Expose flip methods through the forwarded ref.
  //
  // Naming: the "forward/backward" helpers reflect READING intent, not the
  // library's internal index direction. Because we hand the library pages in
  // reversed order, library's flipPrev advances reading forward.
  //
  // Note: `disableFlipByClick={true}` (set on HTMLFlipBook below) blocks the
  // library's `flip(point)` path because programmatic `flipPrev/flipNext`
  // internally simulate a tap at the page edge — and the disable-flag aborts
  // any tap that isn't on a corner. We therefore temporarily lift the flag
  // around our programmatic call, which preserves the smooth animated flip
  // for the on-screen Prev/Next arrows while still preventing unintended
  // center-of-page taps from flipping the book.
  useEffect(() => {
    if (!ref) return;
    const safeFlip = (which) => {
      const flip = bookRef.current?.pageFlip?.();
      if (!flip) return;
      const settings = flip.getSettings?.();
      let prev;
      if (settings) {
        prev = settings.disableFlipByClick;
        settings.disableFlipByClick = false;
      }
      try {
        if (which === "prev") flip.flipPrev();
        else flip.flipNext();
      } finally {
        if (settings) {
          // Restore on next tick so the in-progress flip animation isn't
          // aborted by re-applying the flag mid-flip.
          setTimeout(() => {
            settings.disableFlipByClick = prev;
          }, 60);
        }
      }
    };
    const api = {
      flipForward: () => safeFlip("prev"),  // RTL: forward = library prev
      flipBackward: () => safeFlip("next"), // RTL: backward = library next
      flipNext: () => safeFlip("next"),
      flipPrev: () => safeFlip("prev"),
      goTo: (n) => bookRef.current?.pageFlip?.()?.flip(n),
      /**
       * Deep-link helper: flip the 3D book to the spread that contains
       * `verseNum`. Used by /reader/?v=N navigations (notebook verse
       * cards, search results, etc.) so the user actually sees the
       * verse they followed, not the first page of the chapter.
       *
       * Returns true if the flip was issued, false if the verse wasn't
       * found in the current chapter's pagination.
       */
      goToVerse: (verseNum) => {
        if (!verseNum || !bookRef.current?.pageFlip) return false;
        // Read latest paging inputs through the ref so we never close
        // over a stale `verses` / `pages` array from the first render.
        const { verses: vs, tocPages: tp0, hasPrev: hp, pages: ps } = pagingRef.current || {};
        if (!vs || vs.length === 0 || !ps || ps.length === 0) return false;
        // Recompute the pagination the same way the render does, so we
        // know which semantic page index holds the target verse.
        const tp = buildTextPages({
          verses: vs,
          maxCharsPerPage: 820,
          maxVersesPerPage: 10,
        });
        const textIdx = tp.findIndex((p) =>
          p.variant === "text" && p.verses?.some?.((v) => v.verse === verseNum)
        );
        if (textIdx < 0) return false;
        // Apply the same prefix offsets that `pages` adds during render.
        // No "+1 for prev-cue" anymore — that head page was removed so
        // entering a chapter lands the user on the first verse directly.
        const semanticIndex = (tp0?.length || 0) + textIdx;
        // Library is fed reversed (LTR-emulated RTL) — translate index.
        const renderedIndex = (ps.length - 1) - semanticIndex;
        // Spreads display two pages at a time. The library lands on the
        // spread containing the page we pass, but we want our target
        // verse to land on the RIGHT page of the spread (the side that
        // matches how `startPage = length-1` lands the first verse on
        // the right). Snap to the even page index of the spread so the
        // target verse is the right page of the spread the user lands on.
        const snappedIndex = renderedIndex - (renderedIndex % 2);
        const pf = bookRef.current.pageFlip();
        if (!pf || typeof pf.flip !== "function") return false;
        try {
          pf.flip(snappedIndex);
        } catch {
          return false;
        }
        return true;
      },
    };
    if (typeof ref === "function") ref(api);
    else ref.current = api;
  }, [ref]);

  const textPages = useMemo(
    () =>
      buildTextPages({
        verses,
        maxCharsPerPage: 820,
        maxVersesPerPage: 10,
      }),
    [verses]
  );

  // Semantic model — always natural reading order.
  // Transition pages:
  //   • suffix "next-chapter" cue on the LAST page (if a next chapter exists)
  // No "prev-chapter" cue is prepended: when the user enters a chapter we
  // must land directly on the first verse (semantic page 0). A leading
  // transition page would push the verses one slot back and make
  // `startPage = length-1` open onto the cue instead of the text — that was
  // the "PREVIOUS CHAPTER" landing bug reported by users.
  // Backward navigation across chapters is already covered by:
  //   • the toolbar Prev arrow (Reader.jsx)
  //   • the mobile flip-bar wrap-around (Reader.jsx handleFlipPrev)
  // so the head cue is redundant and was causing real harm.
  const pages = useMemo(() => {
    const combined = [...tocPages, ...textPages];
    const tail = hasNext
      ? [
          {
            variant: "transition",
            transitionDirection: "next",
            transitionTargetChapter: (chapter ?? 1) + 1,
            transitionLabelHe: nextChapterLabelHe,
          },
        ]
      : [];
    const full = [...combined, ...tail];
    if (full.length % 2 !== 0) full.push({ variant: "blank" });
    return full;
  }, [tocPages, textPages, hasNext, chapter, nextChapterLabelHe]);

  // Rendering order for the LTR library = reversed. See file header for why.
  const renderedPages = useMemo(() => [...pages].reverse(), [pages]);
  const startPage = renderedPages.length - 1;

  // Keep the imperative-API ref synced with the latest paging inputs so
  // `goToVerse` always reads fresh data without re-creating the api object.
  useEffect(() => {
    pagingRef.current = { verses, tocPages, hasPrev, pages };
  }, [verses, tocPages, hasPrev, pages]);

  return (
    <div
      ref={stageRef}
      className="book-stage-3d"
      data-testid="book-reader-3d"
      style={{ minHeight: `${dims.height + 120}px` }}
    >
      <div className="tanakh-book-wrapper" style={{ width: "auto" }}>
        <HTMLFlipBook
          ref={bookRef}
          width={dims.width}
          height={dims.height}
          size="fixed"
          minWidth={Math.min(180, dims.width)}
          maxWidth={Math.max(800, dims.width)}
          minHeight={Math.min(220, dims.height)}
          maxHeight={Math.max(1100, dims.height)}
          showCover={false}
          drawShadow={true}
          flippingTime={720}
          usePortrait={dims.portrait}
          startPage={startPage}
          mobileScrollSupport={true}
          maxShadowOpacity={0.6}
          className="tanakh-book"
          style={{}}
          startZIndex={0}
          autoSize={false}
          clickEventForward={true}
          useMouseEvents={true}
          // Higher swipe-distance threshold ⇒ a casual tap or short drag
          // never accidentally turns the page. Only a deliberate swipe
          // crossing ~90 px from a page edge will flip.
          swipeDistance={90}
          showPageCorners={true}
          // Center-click never flips a page. Only the visible corner
          // affordance (or the explicit chapter-nav buttons) can.
          disableFlipByClick={true}
          onFlip={(e) => {
            // No auto-continue: the user must explicitly press the
            // [ לפרק הבא ] / [ לפרק הקודם ] transition button or the
            // toolbar nav arrows. This prevents accidental chapter
            // jumps when interacting with verse content.
            onPageFlip?.(e.data);
          }}
        >
          {renderedPages.map((p, i) => {
            // Semantic (reading-order) index of this page — always counts
            // forward from Bereshit 1:1 regardless of how we reversed for
            // the library. This is what gets shown as the folio number.
            const semanticIndex = renderedPages.length - 1 - i;
            const isTransition = p.variant === "transition";
            const transClick = isTransition
              ? () =>
                  p.transitionDirection === "next"
                    ? onNextChapter?.()
                    : onPrevChapter?.()
              : undefined;
            return (
              <BookPage
                key={`${chapter}-${i}`}
                variant={p.variant}
                bookHe={p.bookHe || bookHe}
                bookEn={p.bookEn || bookEn}
                chapter={p.chapter ?? chapter}
                pageIndex={semanticIndex}
                side={i % 2 === 0 ? "left" : "right"}
                verses={p.verses || []}
                startsChapter={p.startsChapter}
                activeVerseNum={activeVerseNum}
                onVerseClick={onVerseClick}
                highlights={highlights}
                notesByVerse={notesByVerse}
                nikud={nikud}
                showVerseNums={showVerseNums}
                tocContent={p.tocContent}
                chapterTitle={p.chapterTitle}
                transitionDirection={p.transitionDirection}
                transitionTargetChapter={p.transitionTargetChapter}
                transitionTargetChapterHe={
                  chapterLabelFn && p.transitionTargetChapter != null
                    ? chapterLabelFn(p.transitionTargetChapter)
                    : undefined
                }
                chapterLabelHe={
                  chapterLabelFn ? chapterLabelFn(p.chapter ?? chapter) : undefined
                }
                chapterLabelEn={
                  chapterLabelEnFn ? chapterLabelEnFn(p.chapter ?? chapter) : undefined
                }
                transitionLabelHe={p.transitionLabelHe}
                onTransitionClick={transClick}
              />
            );
          })}
        </HTMLFlipBook>
      </div>
    </div>
  );
});

export default BookReader3D;

function computeDims(containerWidth, viewportHeight) {
  // Pull live viewport so we can subtract sticky-chrome + safe-areas. We
  // cap inside CSS too, but the more accurate the JS estimate, the less
  // visual jiggle on first paint.
  const w =
    typeof containerWidth === "number"
      ? containerWidth
      : typeof window !== "undefined"
      ? window.innerWidth
      : 1200;
  const h =
    typeof viewportHeight === "number"
      ? viewportHeight
      : typeof window !== "undefined"
      ? window.innerHeight
      : 900;

  // Read iOS safe-area insets from CSS env() — the values are exposed
  // through CSS custom properties we set on :root in index.css.
  let safeTop = 0;
  let safeBottom = 0;
  if (typeof window !== "undefined") {
    const cs = getComputedStyle(document.documentElement);
    safeTop = parseFloat(cs.getPropertyValue("--sat") || "0") || 0;
    safeBottom = parseFloat(cs.getPropertyValue("--sab") || "0") || 0;
  }

  // Reserved chrome heights. On phones we shrink the toolbar (compact-mobile-toolbar
  // CSS reduces it to ~44px) and reserve ~72px at the bottom for the new
  // sticky page-nav bar so the book never sneaks underneath it.
  const isMobile = w < 640;
  const isTablet = w >= 640 && w < 1024;
  // Phone-landscape: a phone rotated to landscape (e.g. iPhone 14 Pro:
  // 852×393). Width crosses into "tablet" range but the viewport height
  // is genuinely tiny — we must use compact chrome AND aspect ratio
  // tuned to the limited height, otherwise the book overflows the
  // viewport and verses get clipped at the bottom (user-reported bug).
  const isPhoneLandscape = !isMobile && h < 500;
  // Phone portrait keeps a 72px reserve for the sticky page-nav bar at the
  // bottom; phone-landscape and desktop don't have that bar. Tablet uses
  // the larger chrome reserve.
  let chromeY;
  if (isMobile) chromeY = 44 + 36 + 72 + safeTop + safeBottom + 16;
  // Phone-landscape: navbar (~48) + toolbar (~56) + verse-pill row (~24)
  // + breathing buffer (24). The toolbar buttons hit 44px Apple HIG so the
  // toolbar itself is ~56px even with our compact CSS. Under-budgeting
  // here causes the bottom of the book to clip below the viewport.
  else if (isPhoneLandscape) chromeY = 48 + 56 + 24 + safeTop + safeBottom + 16;
  else chromeY = 64 + 64 + safeTop + safeBottom + 16;

  // Phone: portrait single-page focus. Tight 12-px side margin so the
  // book takes maximum width without clipping. Height fits the remaining
  // viewport (after sticky chrome + safe-areas) without ever overflowing.
  if (isMobile) {
    const pageW = Math.min(w - 16, 440);
    // Aspect ratio reduced from 1.46 → 1.38 so the book fits comfortably
    // even on iPhone SE (375×667) without bottom-overflow.
    const idealH = Math.round(pageW * 1.38);
    const pageH = Math.max(360, Math.min(h - chromeY, idealH));
    return { width: pageW, height: pageH, portrait: true };
  }

  // Phone in landscape — fill the ENTIRE screen width with a two-page
  // spread at NORMAL book look (same fonts/padding as desktop). Width
  // is the full viewport. Height is set to a comfortable ~480px so a
  // typical chapter of verses fits at normal font size; if this is taller
  // than the viewport, the page scrolls vertically — preferred over
  // clipping content or shrinking text. The user keeps the "real book"
  // feel and reaches the bottom by scrolling.
  if (isPhoneLandscape) {
    const availW = w - 16; // 8px margin each side
    const pageW = Math.floor(availW / 2);
    const pageH = 480;
    return { width: pageW, height: pageH, portrait: false };
  }

  // Tablet portrait — keep single-page mode for clarity unless landscape.
  if (isTablet) {
    if (h > w) {
      const pageW = Math.min(w - 64, 560);
      const pageH = Math.min(h - chromeY, Math.round(pageW * 1.4));
      return { width: pageW, height: pageH, portrait: true };
    }
    // Landscape tablet — full spread.
    const pageW = Math.min(Math.floor((w * 0.88 - 48) / 2), 560);
    const pageH = Math.min(h - chromeY, Math.round(pageW * 1.34));
    return { width: pageW, height: pageH, portrait: false };
  }

  // Desktop: full two-page spread.
  const pageW = Math.min(Math.floor((w * 0.92 - 64) / 2), 680);
  const pageH = Math.min(h - chromeY, Math.round(pageW * 1.36));
  return { width: pageW, height: pageH, portrait: false };
}

function buildTextPages({ verses, maxCharsPerPage, maxVersesPerPage }) {
  if (!verses || verses.length === 0) return [{ variant: "blank" }];

  const pages = [];
  let current = [];
  let currentChars = 0;

  for (const v of verses) {
    const vChars = (v.he || "").length;
    if (
      current.length > 0 &&
      (current.length >= maxVersesPerPage ||
        currentChars + vChars > maxCharsPerPage)
    ) {
      pages.push({
        variant: "text",
        verses: current,
        startsChapter: pages.length === 0,
      });
      current = [];
      currentChars = 0;
    }
    current.push(v);
    currentChars += vChars;
  }
  if (current.length > 0) {
    pages.push({
      variant: "text",
      verses: current,
      startsChapter: pages.length === 0,
    });
  }
  return pages;
}
